// LiveActivityFeed — pulse strip at the top of the PM Dispatch Board.
// Auto-polls every 10s. New events slide in with a subtle fade so
// nothing on-screen jumps. When shown live during a demo, this is the
// single most convincing feature — Field Radar looks like a nervous
// system, not a form-processor.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Sparkle, Plus, ArrowsClockwise, EnvelopeSimple, PaperPlaneTilt,
  ClockCounterClockwise, ArrowRight, Radio, DotsThreeVertical,
} from "@phosphor-icons/react";

const ICON_MAP = {
  plus:              <Plus                weight="fill" size={14}/>,
  sparkle:           <Sparkle             weight="fill" size={14}/>,
  arrows_clockwise:  <ArrowsClockwise     weight="fill" size={14}/>,
  envelope:          <EnvelopeSimple      weight="fill" size={14}/>,
  paper_plane:       <PaperPlaneTilt      weight="fill" size={14}/>,
  clock:             <ClockCounterClockwise size={14}/>,
};

const ACCENT_MAP = {
  emerald: "bg-emerald-500 text-white",
  fuchsia: "bg-fuchsia-500 text-white",
  cyan:    "bg-cyan-500 text-white",
  indigo:  "bg-indigo-500 text-white",
  amber:   "bg-amber-500 text-slate-900",
  slate:   "bg-slate-600 text-white",
};

const POLL_MS = 10_000;

function timeAgo(iso) {
  try {
    const s = Math.max(1, Math.round((Date.now() - new Date(iso)) / 1000));
    if (s < 60)    return `${s}s ago`;
    if (s < 3600)  return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return "1d+";
  } catch { return "—"; }
}

export default function LiveActivityFeed() {
  const [events, setEvents]   = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow]         = useState(Date.now());  // for relative times to tick
  const seen = useRef(new Set());

  const load = async () => {
    try {
      const r = await api.get("/activity-feed", { params: { limit: 20 } });
      const fresh = r.data.events || [];
      setEvents(fresh);
      fresh.forEach(e => seen.current.add(e.id));
    } catch { /* silent - dashboard shouldn't blow up on feed hiccup */ }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => { clearInterval(iv); clearInterval(tick); };
  }, []);

  if (events.length === 0) return null;

  const shown = expanded ? events : events.slice(0, 4);

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden mb-6" data-testid="live-activity-feed">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-700 inline-flex items-center gap-1.5">
          <Radio weight="fill" size={11} className="text-emerald-600"/> Live activity
        </div>
        <div className="text-[10px] font-medium text-slate-400 hidden sm:inline">— polling every {POLL_MS/1000}s</div>
        <button onClick={() => setExpanded(v => !v)}
          data-testid="feed-toggle"
          className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          {expanded ? "Show 4" : `Show all ${events.length}`}
          <DotsThreeVertical weight="bold" size={12}/>
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {shown.map(e => (
          <Link key={e.id} to={e.link || "#"}
            data-testid={`feed-event-${e.id}`}
            className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition group">
            <div className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${ACCENT_MAP[e.accent] || ACCENT_MAP.slate}`}>
              {ICON_MAP[e.icon] || ICON_MAP.clock}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <div className="font-black text-sm text-slate-900 truncate">{e.title}</div>
                <div className="text-[10px] font-medium text-slate-400 shrink-0" title={e.at}>
                  {timeAgo(e.at)}
                </div>
              </div>
              {e.line1 && <div className="text-[12px] text-slate-600 truncate">{e.line1}</div>}
              {e.line2 && <div className="text-[11px] text-slate-400 truncate">{e.line2}</div>}
            </div>
            <ArrowRight size={14} className="mt-1.5 text-slate-300 group-hover:text-slate-900 shrink-0"/>
          </Link>
        ))}
      </div>
    </div>
  );
}
